const VARIANT_MARKER_REGEX = /<!--\s*ora-variant-id:([a-zA-Z0-9-]+)\s*-->/i;

const stripVariantMarker = (html: string): string =>
  html.replace(VARIANT_MARKER_REGEX, '');

/**
 * Rendered KaTeX is a large tree of spans. Persist the TeX source instead, so
 * the stored stem stays small and re-renders cleanly wherever it is loaded.
 *
 * Lives here (rather than in `highlightDom.ts`, which persists highlights)
 * because `normalizeQuestionHtmlForComparison` below needs it too, to read
 * the TeX source back out of *older* saved copies that still hold rendered
 * KaTeX markup from before this normalization existed. `highlightDom.ts`
 * re-exports it to keep its own callers unchanged.
 */
export const normalizeKatexMarkupForPersistence = (html: string): string => {
  if (!html.includes('katex')) {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const katexNodes = Array.from(container.querySelectorAll('span.katex'));

  katexNodes.forEach((katexNode) => {
    const annotation = katexNode.querySelector(
      '.katex-mathml annotation[encoding="application/x-tex"]'
    );

    const texSource = annotation?.textContent?.trim();

    if (!texSource) {
      return;
    }

    const parentElement = katexNode.parentElement;

    const isDisplayMath = Boolean(
      parentElement?.classList.contains('katex-display')
    );

    const replacementText = isDisplayMath
      ? `\\[${texSource}\\]`
      : `\\(${texSource}\\)`;

    const replacementNode =
      isDisplayMath && parentElement ? parentElement : katexNode;

    replacementNode.replaceWith(document.createTextNode(replacementText));
  });

  return container.innerHTML;
};

const unwrapHighlightSpans = (container: HTMLElement): void => {
  container.querySelectorAll('span.highlight').forEach((node) => {
    const parent = node.parentNode;
    if (!parent) return;
    while (node.firstChild) {
      parent.insertBefore(node.firstChild, node);
    }
    if (node.parentNode === parent) {
      parent.removeChild(node);
    }
  });
};

/**
 * Inline/display math delimiter tokens. The live question stores TeX source
 * as `$…$` / `$$…$$`; a saved highlight copy has it rewritten by
 * `normalizeKatexMarkupForPersistence` to `\(…\)` / `\[…\]`. Stripping both
 * styles from both sides before comparing means that rewrite alone never
 * makes a saved copy look stale. Matching is symmetric, so a literal amount
 * like "$5" loses the same "$" on both sides and still compares equal.
 */
const MATH_DELIMITER_REGEX = /\\\(|\\\)|\\\[|\\\]|\$/g;

const normalizeQuestionHtmlForComparison = (html: string): string => {
  const htmlWithoutMarker = stripVariantMarker(html);

  // Older saved copies were written before `normalizeKatexMarkupForPersistence`
  // existed, so some still hold rendered KaTeX markup rather than TeX source.
  // Convert any of that back to TeX source (a no-op when there is none) on
  // both sides, so the delimiter stripping below treats them the same way.
  const htmlWithTexSource =
    normalizeKatexMarkupForPersistence(htmlWithoutMarker);

  const container = document.createElement('div');
  container.innerHTML = htmlWithTexSource;

  unwrapHighlightSpans(container);

  return (container.textContent ?? '')
    .replace(MATH_DELIMITER_REGEX, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Whole math segments (delimiters and the TeX/rendering between them), used
// only by the legacy-KaTeX prose fallback below -- unlike MATH_DELIMITER_REGEX,
// these drop the content too, not just the delimiters.
const DOLLAR_DISPLAY_MATH_SEGMENT_REGEX = /\$\$[\s\S]*?\$\$/g;
const DOLLAR_INLINE_MATH_SEGMENT_REGEX = /\$[\s\S]*?\$/g;
const BACKSLASH_MATH_SEGMENT_REGEX = /\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)/g;

/**
 * The question stem's surrounding prose, with every math segment gone
 * entirely (not just its delimiters, unlike `normalizeQuestionHtmlForComparison`).
 *
 * A fallback for saved copies written by an even older highlighter, which
 * could split a KaTeX tree's TeX source across several `span.highlight`
 * wrappers inside `.katex-mathml`/`.katex-html`, leaving the `annotation`
 * empty -- there is no reliable TeX source left to read back out of them, so
 * `normalizeQuestionHtmlForComparison` always disagrees on these even when
 * the question hasn't changed. Discarding all math from both sides and
 * comparing only the prose around it recovers those, at the cost of also
 * ignoring a genuine change inside a formula -- acceptable here because this
 * only ever runs once the exact comparison has already failed, and only for
 * a saved copy that actually carries rendered KaTeX; a clean copy keeps
 * getting only the exact comparison, so a real change inside its formula is
 * still always caught.
 */
const normalizeQuestionProseForComparison = (html: string): string => {
  const htmlWithoutMarker = stripVariantMarker(html);
  const container = document.createElement('div');
  container.innerHTML = htmlWithoutMarker;

  container.querySelectorAll('.katex-display, span.katex').forEach((node) => {
    node.remove();
  });

  unwrapHighlightSpans(container);

  return (container.textContent ?? '')
    .replace(DOLLAR_DISPLAY_MATH_SEGMENT_REGEX, '')
    .replace(DOLLAR_INLINE_MATH_SEGMENT_REGEX, '')
    .replace(BACKSLASH_MATH_SEGMENT_REGEX, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const attachVariantMarkerToQuestionHtml = (
  html: string,
  variantId?: string
): string => {
  if (!variantId) return html;
  return `<!--ora-variant-id:${variantId}-->${stripVariantMarker(html)}`;
};

export const doesSchedulingQuestionMatchVariant = ({
  questionHighlights,
  questionHtml,
  variantId,
}: {
  questionHighlights?: string | null;
  questionHtml: string;
  variantId?: string;
}): boolean => {
  if (!questionHighlights) return false;

  const markerMatch = questionHighlights.match(VARIANT_MARKER_REGEX);
  if (markerMatch?.[1]) {
    if (!variantId || markerMatch[1] !== variantId) return false;
  }

  // The marker only proves the saved copy was made for this variant, not that
  // the variant's question text hasn't been fixed since. Compare the visible
  // text too (highlight markup and whitespace stripped), so a saved copy
  // whose underlying question has since been edited on the backend is never
  // rendered in its place.
  if (
    normalizeQuestionHtmlForComparison(questionHighlights) ===
    normalizeQuestionHtmlForComparison(questionHtml)
  ) {
    return true;
  }

  // Only fall back to the looser, math-blind comparison when conversion left
  // a KaTeX span it could not read back (its annotation was empty) -- the
  // unrecoverable split-legacy case this fallback exists for. A saved copy
  // whose rendered KaTeX all converted cleanly relies on the exact
  // comparison above alone, so a real change inside its formula still always
  // fails the match; a copy that mixes a well-formed span with an
  // unconvertible one still gets the fallback, and loses the same
  // guarantee, because both end up with all their math stripped alike.
  const highlightsWithTexSource = normalizeKatexMarkupForPersistence(
    stripVariantMarker(questionHighlights)
  );

  if (!highlightsWithTexSource.includes('class="katex"')) {
    return false;
  }

  return (
    normalizeQuestionProseForComparison(questionHighlights) ===
    normalizeQuestionProseForComparison(questionHtml)
  );
};

export const getRenderableAnswerCrossouts = ({
  answerCrossouts,
  answerOptionCount,
  questionHighlights,
  questionHtml,
  variantId,
}: {
  answerCrossouts?: boolean[] | null;
  answerOptionCount: number;
  questionHighlights?: string | null;
  questionHtml: string;
  variantId?: string;
}): boolean[] => {
  if (!Array.isArray(answerCrossouts) || answerCrossouts.length === 0) {
    return [];
  }

  if (answerOptionCount <= 0) {
    return [];
  }

  if (
    !doesSchedulingQuestionMatchVariant({
      questionHighlights,
      questionHtml,
      variantId,
    })
  ) {
    return [];
  }

  const normalized = answerCrossouts.slice(0, answerOptionCount).map(Boolean);
  while (normalized.length < answerOptionCount) {
    normalized.push(false);
  }
  return normalized;
};

export const stripVariantMarkerFromQuestionHtml = (html: string): string =>
  stripVariantMarker(html);
